/**
 * Token-expired detection for data, file and function responses (JOSH-89).
 *
 * Only rejections that happen before the request runs are recognized, so a
 * replay can never execute a write twice:
 * - PostgREST: 401 with code `PGRST303` (JWT expired) or `PGRST301`;
 * - Storage: 400/403 whose message mentions the `exp` claim or "jwt expired";
 * - Functions gateway: 401 "Invalid JWT".
 * Everything else, including 401s written by a function itself
 * (`verify_jwt=false`, e.g. "Não autenticado"), is "not expired".
 */

import type { RouteKind } from './routes';

const NONE: ReadonlySet<number> = new Set();

/** Statuses whose body is worth reading, per route; any other response is never cloned. */
export const EXPIRY_STATUSES: Record<RouteKind, ReadonlySet<number>> = {
  rest: new Set([401]),
  storage: new Set([400, 403]),
  functions: new Set([401]),
  auth: NONE,
  other: NONE,
};

const POSTGREST_EXPIRED_CODES: ReadonlySet<string> = new Set(['PGRST303', 'PGRST301']);
const STORAGE_EXPIRED_MESSAGE = /"?\bexp\b"? claim|jwt expired/i;

function field(body: unknown, key: string): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/** Whether a parsed error body is one of the known token-expired shapes. */
export function isTokenExpired(kind: RouteKind, status: number, body: unknown): boolean {
  switch (kind) {
    case 'rest':
      return status === 401 && POSTGREST_EXPIRED_CODES.has(field(body, 'code') ?? '');
    case 'storage':
      return (
        (status === 400 || status === 403) &&
        [field(body, 'message'), field(body, 'error')].some(
          (text) => text !== undefined && STORAGE_EXPIRED_MESSAGE.test(text),
        )
      );
    case 'functions':
      return status === 401 && field(body, 'message')?.trim().toLowerCase() === 'invalid jwt';
    default:
      return false;
  }
}

/** Reads an error body from a clone, leaving the original response untouched. */
export async function readErrorBody(response: Response): Promise<unknown> {
  try {
    const text = await response.clone().text();
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return { message: text };
    }
  } catch {
    return null;
  }
}
